import React, { useState, useEffect } from 'react';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CurrencyInput from '@/components/CurrencyInput';
import WorkflowStepper from '@/components/WorkflowStepper';
import FieldHelp from '@/components/FieldHelp';
import { Sparkle, Plus, Trash } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import MilestoneCelebration from '@/components/MilestoneCelebration';
import { useMilestoneCheck } from '@/hooks/useMilestoneCheck';

const ASSET_FIELDS = [
  {
    key: 'cash',
    label: 'Cash & Savings',
    help: {
      title: 'Cash & Savings',
      body: 'Total balance across checking and savings accounts — money you can access immediately for living expenses or an emergency.',
      suggestion: 'Aim for 3–6 months of expenses set aside as emergency savings.',
    },
  },
  {
    key: 'traditional_401k',
    label: 'Traditional 401(k)',
    help: {
      title: 'Traditional 401(k)',
      body: 'Employer-sponsored retirement account funded with pre-tax contributions. Withdrawals in retirement are taxed as ordinary income.',
      suggestion: 'Enter the current balance from your most recent 401(k) statement.',
    },
  },
  {
    key: 'traditional_ira',
    label: 'Traditional IRA',
    help: {
      title: 'Traditional IRA',
      body: 'Individual retirement account funded with pre-tax dollars (if deductible). Like a Traditional 401(k), withdrawals are taxed as ordinary income.',
      suggestion: 'Enter the current balance from your IRA brokerage statement.',
    },
  },
  {
    key: 'roth_401k',
    label: 'Roth 401(k)',
    help: {
      title: 'Roth 401(k)',
      body: 'Employer plan funded with after-tax dollars. Qualified withdrawals (age 59½ + 5-year rule) are generally tax-free.',
      suggestion: 'Enter the current balance — Roth 401(k) is usually shown as a separate line on your statement.',
    },
  },
  {
    key: 'roth_ira',
    label: 'Roth IRA',
    help: {
      title: 'Roth IRA',
      body: 'Individual retirement account funded with after-tax dollars. Qualified withdrawals are generally tax-free in retirement, making this a powerful long-term wealth tool.',
      suggestion: 'Enter the current balance from your Roth IRA statement.',
    },
  },
  {
    key: 'taxable_brokerage',
    label: 'Taxable Brokerage',
    help: {
      title: 'Taxable Brokerage Account',
      body: 'Standard investment account (non-retirement). No tax advantage on contributions, but capital gains rates may apply on long-term sales.',
      suggestion: 'Enter the current market value of stocks, ETFs, mutual funds, etc.',
    },
  },
  {
    key: 'real_estate',
    label: 'Real Estate',
    help: {
      title: 'Real Estate',
      body: 'Estimated current market value of property you own (primary residence, rental, vacation home, raw land).',
      suggestion: 'Use a recent Zillow/Redfin estimate or an appraisal — refresh annually.',
    },
  },
  {
    key: 'business_equity',
    label: 'Business Equity',
    help: {
      title: 'Business Equity',
      body: 'Your ownership stake in a privately held business, partnership, or LLC. Use a defensible valuation (book value, recent funding round, or buy-sell agreement).',
      suggestion: 'If unsure, leave at 0 and consult your CPA for a valuation method.',
    },
  },
];

const NetWorthTracker = () => {
  useTrackToolUsage('net_worth');
  const [entries, setEntries] = useState([]);
  const [formData, setFormData] = useState({
    cash: null, traditional_401k: null, traditional_ira: null,
    roth_401k: null, roth_ira: null, taxable_brokerage: null,
    real_estate: null, business_equity: null, liabilities: null
  });
  const { celebrations, triggerCheck, dismissAll } = useMilestoneCheck(false);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await api.get('/net-worth');
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load net worth data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      Object.keys(formData).forEach(key => {
        const v = formData[key];
        payload[key] = (v === null || v === undefined || Number.isNaN(v)) ? 0 : Number(v);
      });
      await api.post('/net-worth', payload);
      toast.success('Net worth snapshot saved!');
      setFormData({
        cash: null, traditional_401k: null, traditional_ira: null,
        roth_401k: null, roth_ira: null, taxable_brokerage: null,
        real_estate: null, business_equity: null, liabilities: null
      });
      fetchEntries();
      // Trigger milestone check after save
      triggerCheck();
    } catch (error) {
      toast.error('Failed to save snapshot');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/net-worth/${id}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const computeAssets = (entry) => ASSET_FIELDS.reduce((sum, f) => sum + (entry[f.key] || 0), 0);
  const computeNetWorth = (entry) => computeAssets(entry) - (entry.liabilities || 0);

  const chartData = entries.map(e => ({
    date: new Date(e.snapshot_date).toLocaleDateString(),
    netWorth: computeNetWorth(e),
    assets: computeAssets(e),
    liabilities: e.liabilities || 0
  }));

  const latestEntry = entries[entries.length - 1];
  const previousEntry = entries[entries.length - 2];
  const currentNetWorth = latestEntry ? computeNetWorth(latestEntry) : 0;
  const previousNetWorth = previousEntry ? computeNetWorth(previousEntry) : 0;
  const change = currentNetWorth - previousNetWorth;
  const changePercent = previousNetWorth !== 0 ? (change / Math.abs(previousNetWorth)) * 100 : 0;

  const assetBreakdown = latestEntry ? ASSET_FIELDS.map(f => ({
    name: f.label,
    value: latestEntry[f.key] || 0
  })).filter(d => d.value > 0) : [];

  return (
    <div className="space-y-8 fade-in">
      {celebrations.length > 0 && (
        <MilestoneCelebration
          celebrations={celebrations}
          onClose={dismissAll}
          onAcknowledgeAll={dismissAll}
        />
      )}
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Tool:</strong> Track your net worth over time. Manual entries provide snapshots for educational analysis only.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Net Worth Tracking
        </h1>
        <p className="text-base text-[#4A5568]">Track your assets and liabilities over time</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="current-net-worth">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Current Net Worth</p>
          <p className="text-3xl font-semibold text-[#0A1128] ibm-plex">${currentNetWorth.toLocaleString()}</p>
          {previousEntry && (
            <p className={`text-sm mt-2 ${change >= 0 ? 'text-[#2D6A4F]' : 'text-[#C53030]'}`}>
              {change >= 0 ? '↗' : '↘'} ${Math.abs(change).toLocaleString()} ({changePercent.toFixed(1)}%)
            </p>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Total Assets</p>
          <p className="text-3xl font-semibold text-[#2D6A4F] ibm-plex">${latestEntry ? computeAssets(latestEntry).toLocaleString() : '0'}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Total Liabilities</p>
          <p className="text-3xl font-semibold text-[#C53030] ibm-plex">${latestEntry ? (latestEntry.liabilities || 0).toLocaleString() : '0'}</p>
        </div>
      </div>

      {chartData.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="net-worth-history-chart">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Net Worth Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" stroke="#4A5568" style={{ fontSize: '12px' }} />
              <YAxis stroke="#4A5568" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="netWorth" stroke="#C5A059" strokeWidth={3} name="Net Worth" />
              <Line type="monotone" dataKey="assets" stroke="#2D6A4F" strokeWidth={2} name="Assets" />
              <Line type="monotone" dataKey="liabilities" stroke="#C53030" strokeWidth={2} name="Liabilities" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="net-worth-form">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Add Snapshot</h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ASSET_FIELDS.map(field => (
                <div key={field.key}>
                  <Label htmlFor={field.key} className="text-xs flex items-center gap-1.5">
                    {field.label} ($)
                    {field.help && <FieldHelp testid={`help-${field.key}`} title={field.help.title} body={field.help.body} suggestion={field.help.suggestion} />}
                  </Label>
                  <CurrencyInput
                    testid={`net-worth-${field.key}-input`}
                    placeholder="$0"
                    value={formData[field.key]}
                    onValue={(v) => setFormData({ ...formData, [field.key]: v })}
                    allowEmpty
                    decimals
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <Label htmlFor="liabilities" className="text-xs flex items-center gap-1.5">
                  Liabilities ($)
                  <FieldHelp
                    testid="help-liabilities"
                    title="Total Liabilities"
                    body="The sum of everything you owe: mortgage balance, credit card balances, student loans, auto loans, personal loans, lines of credit, and any other debt."
                    suggestion="Pull the totals from your most recent statements. Net worth = Assets − Liabilities."
                  />
                </Label>
                <CurrencyInput
                  testid="net-worth-liabilities-input"
                  placeholder="Mortgage, credit cards, loans, etc."
                  value={formData.liabilities}
                  onValue={(v) => setFormData({ ...formData, liabilities: v })}
                  allowEmpty
                  decimals
                />
              </div>
            </div>
            <Button type="submit" data-testid="save-net-worth-button" className="w-full bg-[#0A1128] hover:bg-[#152145] mt-4">
              <Plus size={18} className="mr-2" />
              Save Snapshot
            </Button>
          </form>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="asset-breakdown-chart">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Current Asset Breakdown</h2>
          {assetBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={assetBreakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#4A5568" />
                <YAxis type="category" dataKey="name" stroke="#4A5568" width={130} style={{ fontSize: '11px' }} />
                <Tooltip />
                <Bar dataKey="value" fill="#C5A059" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-[#4A5568] py-12">Add a snapshot to see breakdown</p>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="net-worth-history-table">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">History</h2>
        {entries.length === 0 ? (
          <p className="text-center text-[#4A5568] py-8">No snapshots yet. Add your first snapshot above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Date</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Assets</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Liabilities</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Net Worth</th>
                  <th className="text-right py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {[...entries].reverse().map((entry) => (
                  <tr key={entry.id} className="border-b border-slate-100">
                    <td className="py-3 px-4 text-sm">{new Date(entry.snapshot_date).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm text-right text-[#2D6A4F] ibm-plex">${computeAssets(entry).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-right text-[#C53030] ibm-plex">${(entry.liabilities || 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-right font-semibold text-[#0A1128] ibm-plex">${computeNetWorth(entry).toLocaleString()}</td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleDelete(entry.id)}
                        data-testid={`delete-net-worth-${entry.id}`}
                        className="text-[#C53030] hover:text-[#9B2C2C]"
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <WorkflowStepper testid="networth-workflow-stepper" />
    </div>
  );
};

export default NetWorthTracker;
